import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  computeMonthlyProjection,
  computeProjection,
  DEFAULT_INPUTS,
  fmtKr,
  type CalcInputs,
  type ProjectionSummary,
} from "../lib/atomika-calc";
import { api, type Scenario } from "../lib/api";
import type { ChartPoint } from "../components/reiknivel/SavingsChart";
import { RummetraMaeling } from "../components/reiknivel/RummetraMaeling";
import { FlotiKynning } from "../components/reiknivel/FlotiKynning";
import { CalcTabBar } from "../components/reiknivel/CalcTabBar";
import { firstVisibleTab, type CalcTab } from "../lib/calc-tabs";
import {
  TilbodsgerdWizard,
  type OfferModel,
} from "../components/reiknivel/TilbodsgerdWizard";

type ReiknivelSearch = {
  kennitala?: string;
  opportunityId?: number;
};

function parseReiknivelSearch(
  search: Record<string, unknown>,
): ReiknivelSearch {
  const rawKt = search.kennitala;
  let kennitala: string | undefined;
  if (typeof rawKt === "string" && rawKt.trim() !== "") {
    const t = rawKt.trim();
    if (t.startsWith('"') && t.endsWith('"')) {
      try {
        const parsed = JSON.parse(t) as unknown;
        if (typeof parsed === "string" && parsed.trim() !== "") {
          kennitala = parsed.trim();
        }
      } catch {
        kennitala = t.slice(1, -1).trim() || undefined;
      }
    } else {
      kennitala = t;
    }
  }
  const raw = search.opportunityId;
  let opportunityId: number | undefined;
  if (typeof raw === "number" && Number.isInteger(raw) && raw > 0) {
    opportunityId = raw;
  } else if (typeof raw === "string" && /^\d+$/.test(raw)) {
    const n = Number(raw);
    if (n > 0) opportunityId = n;
  }
  return { kennitala, opportunityId };
}

export const Route = createFileRoute("/verkfaeri/atomika-reiknivel")({
  validateSearch: parseReiknivelSearch,
  component: CalculatorPage,
});

const ATOMIKA_BLUE = "#045AFF";
const C_COMPANY = "#045AFF";

type Tab = "tilbod" | "rummetra" | "flotinn" | "innri" | "aaetlanir";

const CALC_TABS: CalcTab[] = [
  { id: "flotinn", label: "Lifandi vöktun" },
  { id: "tilbod", label: "Tilboðsgerð" },
  { id: "innri", label: "Innri", locked: true },
];

function modelFrom(inputs: CalcInputs): OfferModel {
  const projection = computeProjection(inputs);
  return {
    inputs,
    summary: projection.summary,
    rows: projection.rows,
    monthPoints: computeMonthlyProjection(inputs).map((p) => ({
      x: p.month,
      performancePct: p.performancePct,
      grossSavings: p.grossSavings,
      equipmentPayment: p.equipmentPayment,
      netSavings: p.netSavings,
      customerBenefit: p.customerBenefit,
      companyRevenue: p.companyRevenue,
    })),
  };
}

function CalculatorPage() {
  const search = Route.useSearch();
  const [tab, setTab] = useState<Tab>(
    () => firstVisibleTab(CALC_TABS, "flotinn") as Tab,
  );
  const [plansFrom, setPlansFrom] = useState<Tab>("flotinn");
  const [model, setModel] = useState<OfferModel>(() =>
    modelFrom(DEFAULT_INPUTS),
  );
  const [step1Done, setStep1Done] = useState(false);
  const [scenarioLoad, setScenarioLoad] = useState<{
    n: number;
    customerName: string;
    inputs: CalcInputs;
  } | null>(null);
  const [pitchErr, setPitchErr] = useState<string | null>(null);

  const [laufidEnabled, setLaufidEnabled] = useState(false);
  const [laufidPct, setLaufidPct] = useState(30);
  const [atomikaHours, setAtomikaHours] = useState(25);
  const [atomikaHourRate, setAtomikaHourRate] = useState(16000);
  const [discountRate, setDiscountRate] = useState(8);
  const [showMonthlyRevenue, setShowMonthlyRevenue] = useState(false);
  const [actualSavingsPct, setActualSavingsPct] = useState(22);

  const [scenarios, setScenarios] = useState<Scenario[]>([]);

  const onModel = useCallback((next: OfferModel) => {
    setModel(next);
  }, []);
  const onStep1Done = useCallback((done: boolean) => {
    setStep1Done(done);
  }, []);

  const refreshScenarios = useCallback(() => {
    api
      .listScenarios()
      .then(setScenarios)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshScenarios();
  }, [refreshScenarios]);

  useEffect(() => {
    if (!search.kennitala || search.opportunityId == null) {
      setPitchErr(null);
      return;
    }
    let active = true;
    api
      .getCompany(search.kennitala)
      .then((detail) => {
        if (!active) return;
        const opp = detail.opportunities.find(
          (o) => o.id === search.opportunityId,
        );
        if (opp?.status === "open") setPitchErr(null);
        else {
          setPitchErr(
            opp
              ? "Tækifærið er ekki opið — reiknivélin er ekki tengd því."
              : "Tækifærið fannst ekki — reiknivélin er ekki tengd CRM.",
          );
        }
      })
      .catch(() => {
        if (active) {
          setPitchErr("Tækifærið fannst ekki — reiknivélin er ekki tengd CRM.");
        }
      });
    return () => {
      active = false;
    };
  }, [search.kennitala, search.opportunityId]);

  const handleDelete = (id: number) => {
    setScenarios((list) => list.filter((s) => s.id !== id));
    api.deleteScenario(id).catch(() => refreshScenarios());
  };

  const handleLoad = (loaded: CalcInputs, customerName: string) => {
    setScenarioLoad({ n: Date.now(), customerName, inputs: loaded });
    setTab("tilbod");
  };

  const { inputs, summary, rows, monthPoints } = model;
  const companySharePct = 100 - inputs.customerSharePct;

  return (
    <div className="mx-auto max-w-[1280px] px-6 py-6 sm:px-7">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <CalcTabBar
          tabs={CALC_TABS}
          activeId={tab === "aaetlanir" ? "tilbod" : tab}
          doneIds={step1Done ? ["tilbod"] : []}
          onSelect={(id) => setTab(id as Tab)}
        />

        <button
          onClick={() => {
            if (tab === "aaetlanir") setTab(plansFrom);
            else {
              setPlansFrom(tab);
              setTab("aaetlanir");
            }
          }}
          className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors ${
            tab === "aaetlanir"
              ? "border-slate-300 bg-slate-100 text-slate-800"
              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
          }`}
        >
          {tab === "aaetlanir" ? "Til baka" : "Vistaðar áætlanir"}
          {scenarios.length > 0 && tab !== "aaetlanir" && (
            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-600">
              {scenarios.length}
            </span>
          )}
        </button>
      </div>

      {pitchErr && (
        <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          {pitchErr}
        </p>
      )}

      <div className={tab === "tilbod" ? "" : "hidden"}>
        <TilbodsgerdWizard
          initialKennitala={search.kennitala}
          scenarioLoad={scenarioLoad}
          visible={tab === "tilbod"}
          onModel={onModel}
          onStep1Done={onStep1Done}
          onScenarioSaved={refreshScenarios}
        />
      </div>

      {tab !== "tilbod" && (
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
          >
            {tab === "rummetra" ? (
              <RummetraMaeling
                companyName={inputs.customerName}
                monthlyCost={inputs.monthlyCost}
              />
            ) : tab === "flotinn" ? (
              <FlotiKynning />
            ) : tab === "innri" ? (
              <InnriTab
                inputs={inputs}
                summary={summary}
                rows={rows}
                monthPoints={monthPoints}
                companySharePct={companySharePct}
                laufidEnabled={laufidEnabled}
                setLaufidEnabled={setLaufidEnabled}
                laufidPct={laufidPct}
                setLaufidPct={setLaufidPct}
                atomikaHours={atomikaHours}
                setAtomikaHours={setAtomikaHours}
                atomikaHourRate={atomikaHourRate}
                setAtomikaHourRate={setAtomikaHourRate}
                discountRate={discountRate}
                setDiscountRate={setDiscountRate}
                showMonthlyRevenue={showMonthlyRevenue}
                setShowMonthlyRevenue={setShowMonthlyRevenue}
                actualSavingsPct={actualSavingsPct}
                setActualSavingsPct={setActualSavingsPct}
              />
            ) : (
              <SavedScenarios
                scenarios={scenarios}
                onLoad={handleLoad}
                onDelete={handleDelete}
              />
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

function InnriTab({
  inputs,
  summary,
  rows,
  monthPoints,
  companySharePct,
  laufidEnabled,
  setLaufidEnabled,
  laufidPct,
  setLaufidPct,
  atomikaHours,
  setAtomikaHours,
  atomikaHourRate,
  setAtomikaHourRate,
  discountRate,
  setDiscountRate,
  showMonthlyRevenue,
  setShowMonthlyRevenue,
  actualSavingsPct,
  setActualSavingsPct,
}: {
  inputs: CalcInputs;
  summary: ProjectionSummary;
  rows: { year: number; companyRevenue: number }[];
  monthPoints: ChartPoint[];
  companySharePct: number;
  laufidEnabled: boolean;
  setLaufidEnabled: (v: (p: boolean) => boolean) => void;
  laufidPct: number;
  setLaufidPct: (v: number) => void;
  atomikaHours: number;
  setAtomikaHours: (v: number) => void;
  atomikaHourRate: number;
  setAtomikaHourRate: (v: number) => void;
  discountRate: number;
  setDiscountRate: (v: number) => void;
  showMonthlyRevenue: boolean;
  setShowMonthlyRevenue: (v: (p: boolean) => boolean) => void;
  actualSavingsPct: number;
  setActualSavingsPct: (v: number) => void;
}) {
  const horizonMonths = inputs.horizonYears * 12;
  const atomikaRevenue = summary.totalCompanyRevenue;
  const salesCost = Math.max(0, atomikaHours * atomikaHourRate);
  const framlegd = atomikaRevenue - salesCost;
  const perMonth = horizonMonths > 0 ? framlegd / horizonMonths : 0;
  const perYear = inputs.horizonYears > 0 ? framlegd / inputs.horizonYears : 0;
  const roi = salesCost > 0 ? (framlegd / salesCost) * 100 : null;

  const r = discountRate / 100;
  const npvRevenue = rows.reduce(
    (s, row) => s + row.companyRevenue / Math.pow(1 + r, row.year),
    0,
  );
  const npvFramlegd = npvRevenue - salesCost;
  const npvDiff =
    atomikaRevenue > 0
      ? ((npvRevenue - atomikaRevenue) / atomikaRevenue) * 100
      : 0;

  const revenueMonthFirst =
    monthPoints.findIndex((m) => m.companyRevenue > 0) + 1;

  const companyShareFrac = companySharePct / 100;
  const customerShareFrac = inputs.customerSharePct / 100;
  const annualGrossPromised =
    inputs.monthlyCost * 12 * (inputs.savingsPct / 100);
  const annualGrossActual = inputs.monthlyCost * 12 * (actualSavingsPct / 100);
  const grossDiff = annualGrossActual - annualGrossPromised;
  const atomikaAdjustment = grossDiff * companyShareFrac;
  const customerAdjustment = grossDiff * customerShareFrac;
  const isCredit = atomikaAdjustment < 0;

  const laufidShare = laufidEnabled ? atomikaRevenue * (laufidPct / 100) : 0;
  const atomikaEftirLaufid = laufidEnabled
    ? atomikaRevenue * (1 - laufidPct / 100) - salesCost
    : framlegd;
  const laufidPerMonth = horizonMonths > 0 ? laufidShare / horizonMonths : 0;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-center gap-2 px-1">
        <svg
          className="h-4 w-4 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span className="text-sm font-medium text-slate-400">
          Innri mælaborð — ekki sýna viðskiptavinum
        </span>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="text-base font-bold text-slate-900">
            Framlegð Atomika
          </span>
          <span className="text-xs font-normal text-slate-400">
            — yfir {inputs.horizonYears} ár ({horizonMonths} mánuðir)
          </span>
        </div>
        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">
              Tekjuhlutfall ({companySharePct}%) yfir {inputs.horizonYears} ár
            </span>
            <span className="font-medium tabular-nums text-slate-700">
              +{fmtKr(atomikaRevenue)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">
              − Sölu- og vinnustundakostnaður
            </span>
            <span className="font-medium tabular-nums text-red-500">
              −{fmtKr(salesCost)}
            </span>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-1.5">
            <span className="font-semibold text-slate-800">
              Framlegð Atomika
            </span>
            <span
              className={`font-bold tabular-nums ${
                framlegd >= 0 ? "text-slate-900" : "text-red-500"
              }`}
            >
              {framlegd >= 0 ? "" : "−"}
              {fmtKr(Math.abs(framlegd))}
            </span>
          </div>
          <p className="pt-0.5 text-[11px] text-slate-400">
            Búnaður er greiddur af viðskiptavini og telst ekki með í framlegð
            Atomika.
          </p>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              Vinnustundir (sala + uppsetn.)
            </span>
            <input
              type="number"
              min={0}
              value={atomikaHours}
              onChange={(e) =>
                setAtomikaHours(
                  e.target.value === ""
                    ? 0
                    : Math.max(0, Number(e.target.value)),
                )
              }
              className="w-full rounded-lg px-3 py-2 text-sm tabular-nums outline-none ring-1 ring-slate-200 focus:ring-2 focus:ring-slate-400"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              Taxti (kr/klst)
            </span>
            <input
              type="number"
              min={0}
              step={500}
              value={atomikaHourRate}
              onChange={(e) =>
                setAtomikaHourRate(
                  e.target.value === ""
                    ? 0
                    : Math.max(0, Number(e.target.value)),
                )
              }
              className="w-full rounded-lg px-3 py-2 text-sm tabular-nums outline-none ring-1 ring-slate-200 focus:ring-2 focus:ring-slate-400"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            {
              label: "Framlegð samtals",
              val: framlegd,
              fmt: (v: number) => `${v >= 0 ? "" : "−"}${fmtKr(Math.abs(v))}`,
            },
            {
              label: "Per mánuð",
              val: perMonth,
              fmt: (v: number) => `${v >= 0 ? "" : "−"}${fmtKr(Math.abs(v))}`,
            },
            {
              label: roi !== null ? "ROI" : "Per ár",
              val: roi ?? perYear,
              fmt: (v: number) =>
                roi !== null
                  ? `${v >= 0 ? "+" : ""}${Math.round(v)}%`
                  : fmtKr(Math.abs(v)),
            },
          ].map(({ label, val, fmt }) => (
            <div
              key={label}
              className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-center"
            >
              <p className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                {label}
              </p>
              <p
                className={`text-base font-bold tabular-nums ${
                  val >= 0 ? "text-emerald-600" : "text-red-500"
                }`}
              >
                {fmt(val)}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-600">
              Núvirðing (ávöxtunarkrafa)
            </span>
            <span className="text-sm font-bold tabular-nums text-slate-700">
              {discountRate}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={20}
            step={0.5}
            value={discountRate}
            onChange={(e) => setDiscountRate(Number(e.target.value))}
            className="h-2 w-full cursor-pointer appearance-none rounded-full accent-slate-500"
          />
          <div className="mb-3 mt-0.5 flex justify-between text-[10px] text-slate-400">
            <span>0%</span>
            <span>20%</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-center">
              <p className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                Núvirtar tekjur (NPV)
              </p>
              <p className="text-base font-bold tabular-nums text-slate-800">
                {fmtKr(npvRevenue)}
              </p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                {npvDiff >= 0 ? "+" : ""}
                {npvDiff.toFixed(1)}% vs nafnvirði
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-center">
              <p className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                Núvirt framlegð
              </p>
              <p
                className={`text-base font-bold tabular-nums ${
                  npvFramlegd >= 0 ? "text-emerald-600" : "text-red-500"
                }`}
              >
                {npvFramlegd >= 0 ? "" : "−"}
                {fmtKr(Math.abs(npvFramlegd))}
              </p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                eftir sölukostnað
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-semibold text-slate-900">
              Mánaðarleg tekjufærsla
            </span>
            <p className="mt-0.5 text-xs text-slate-400">
              Hvað Atomika tekjufærir hvern mánuð yfir samningstímann
            </p>
          </div>
          <button
            onClick={() => setShowMonthlyRevenue((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-slate-600"
            aria-pressed={showMonthlyRevenue}
          >
            <span
              className={
                showMonthlyRevenue ? "text-blue-700" : "text-slate-400"
              }
            >
              {showMonthlyRevenue ? "Sýnt" : "Falið"}
            </span>
            <span
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                showMonthlyRevenue ? "bg-blue-500" : "bg-slate-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                  showMonthlyRevenue ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </span>
          </button>
        </div>

        {showMonthlyRevenue && (
          <div className="mt-4">
            {revenueMonthFirst > 1 && (
              <p className="mb-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-600">
                ⚠ Engin tekjufærsla fyrstu {revenueMonthFirst - 1} mánuðina —
                allur sparnaður fer í að greiða upp búnaðinn. Tekjufærsla hefst
                í mánuði {revenueMonthFirst}.
              </p>
            )}
            <div className="max-h-64 overflow-x-auto overflow-y-auto rounded-lg border border-slate-100">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-100 text-left text-slate-500">
                    <th className="px-3 py-2 font-medium">Mán.</th>
                    <th className="px-3 py-2 text-right font-medium">Brúttó</th>
                    <th className="px-3 py-2 text-right font-medium">
                      Tekjufærsla Atomika
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {monthPoints.map((m) => (
                    <tr
                      key={m.x}
                      className={`border-b border-slate-50 last:border-0 ${
                        m.companyRevenue === 0 ? "text-slate-400" : ""
                      }`}
                    >
                      <td className="px-3 py-1.5 tabular-nums">{m.x}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                        {fmtKr(m.grossSavings)}
                      </td>
                      <td
                        className="px-3 py-1.5 text-right font-medium tabular-nums"
                        style={{
                          color: m.companyRevenue > 0 ? C_COMPANY : undefined,
                        }}
                      >
                        {m.companyRevenue > 0 ? fmtKr(m.companyRevenue) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm">
        <div className="mb-1 flex items-center gap-2">
          <span className="font-semibold text-slate-900">Ársleiðrétting</span>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Salan byggir á áætlaðri lækkun. Í árslok er gert upp við raunlækkun —
          kreditreikningur ef minna næst, viðbótarreikningur ef meira.
        </p>

        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-100 bg-white px-3 py-2.5">
            <p className="mb-1 text-[10px] uppercase tracking-wide text-slate-400">
              Lofuð lækkun (áætlun)
            </p>
            <p className="text-lg font-bold tabular-nums text-slate-700">
              {inputs.savingsPct}%
            </p>
            <p className="mt-0.5 text-[10px] text-slate-400">
              {fmtKr(annualGrossPromised)}/ár brúttó
            </p>
          </div>
          <label className="block rounded-xl border border-amber-200 bg-white px-3 py-2.5">
            <span className="mb-1 block text-[10px] uppercase tracking-wide text-amber-600">
              Raunlækkun (mæld)
            </span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={actualSavingsPct}
                onChange={(e) =>
                  setActualSavingsPct(
                    e.target.value === ""
                      ? 0
                      : Math.max(0, Math.min(100, Number(e.target.value))),
                  )
                }
                className="w-full bg-transparent text-lg font-bold tabular-nums text-slate-900 outline-none"
              />
              <span className="text-lg font-bold text-slate-400">%</span>
            </div>
            <p className="mt-0.5 text-[10px] text-slate-400">
              {fmtKr(annualGrossActual)}/ár brúttó
            </p>
          </label>
        </div>

        <div
          className={`rounded-xl border px-4 py-3 ${
            Math.abs(atomikaAdjustment) < 1
              ? "border-slate-200 bg-slate-50"
              : isCredit
                ? "border-red-200 bg-red-50"
                : "border-emerald-200 bg-emerald-50"
          }`}
        >
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              Mismunur á brúttó sparnaði (árlega)
            </span>
            <span
              className={`font-medium tabular-nums ${
                grossDiff >= 0 ? "text-emerald-600" : "text-red-500"
              }`}
            >
              {grossDiff >= 0 ? "+" : "−"}
              {fmtKr(Math.abs(grossDiff))}
            </span>
          </div>
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              Leiðrétting til viðskiptavinar ({inputs.customerSharePct}%)
            </span>
            <span className="font-medium tabular-nums text-slate-600">
              {customerAdjustment >= 0 ? "+" : "−"}
              {fmtKr(Math.abs(customerAdjustment))}
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-slate-200/60 pt-2">
            <span className="font-semibold text-slate-800">
              {Math.abs(atomikaAdjustment) < 1
                ? "Engin leiðrétting"
                : isCredit
                  ? "Kreditreikningur í árslok"
                  : "Viðbótarreikningur í árslok"}
            </span>
            <span
              className={`text-lg font-bold tabular-nums ${
                Math.abs(atomikaAdjustment) < 1
                  ? "text-slate-500"
                  : isCredit
                    ? "text-red-600"
                    : "text-emerald-600"
              }`}
            >
              {isCredit ? "−" : Math.abs(atomikaAdjustment) < 1 ? "" : "+"}
              {fmtKr(Math.abs(atomikaAdjustment))}
            </span>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          Leiðrétting Atomika = {companySharePct}% af mismun á árssparnaði.
          Byggir á árlegum brúttó sparnaði við full afköst.
        </p>
      </div>

      <div
        className={`rounded-2xl border p-5 shadow-sm transition-colors ${
          laufidEnabled
            ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"
            : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="font-semibold text-slate-900">Laufið umboð</span>
          </div>
          <button
            onClick={() => setLaufidEnabled((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-slate-600"
            aria-pressed={laufidEnabled}
          >
            <span
              className={laufidEnabled ? "text-emerald-700" : "text-slate-400"}
            >
              {laufidEnabled ? "Laufið er söluaðili" : "Atomika selur beint"}
            </span>
            <span
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                laufidEnabled ? "bg-emerald-500" : "bg-slate-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                  laufidEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </span>
          </button>
        </div>

        {laufidEnabled ? (
          <div className="space-y-4">
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600">
                  Umboðsþóknun Laufið
                </span>
                <span
                  className="text-sm font-bold tabular-nums"
                  style={{ color: "#10B981" }}
                >
                  {laufidPct}%
                </span>
              </div>
              <input
                type="range"
                min={5}
                max={50}
                step={1}
                value={laufidPct}
                onChange={(e) => setLaufidPct(Number(e.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-full accent-emerald-500"
              />
              <div className="mt-0.5 flex justify-between text-[10px] text-slate-400">
                <span>5%</span>
                <span>50%</span>
              </div>
            </div>

            <div className="space-y-1.5 border-t border-emerald-100 pt-2 text-sm">
              <div className="flex items-center justify-between text-slate-500">
                <span>Heildartekjur úr tekjuhlutfalli</span>
                <span className="font-medium tabular-nums text-slate-700">
                  {fmtKr(atomikaRevenue)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-emerald-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Laufið fær ({laufidPct}%)
                </span>
                <span className="font-semibold tabular-nums text-emerald-700">
                  {fmtKr(laufidShare)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-blue-700">
                  <span className="h-2 w-2 rounded-full bg-blue-500" />
                  Atomika fær ({100 - laufidPct}%)
                </span>
                <span className="font-semibold tabular-nums text-blue-700">
                  {fmtKr(atomikaRevenue * (1 - laufidPct / 100))}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-100/60 px-3 py-2.5 text-center">
                <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                  Laufið — per mánuð
                </p>
                <p className="text-base font-bold tabular-nums text-emerald-700">
                  {fmtKr(laufidPerMonth)}
                </p>
                <p className="mt-0.5 text-[10px] text-emerald-600">
                  Samtals {fmtKr(laufidShare)}
                </p>
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-center">
                <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-600">
                  Atomika — framlegð
                </p>
                <p
                  className={`text-base font-bold tabular-nums ${
                    atomikaEftirLaufid >= 0 ? "text-blue-700" : "text-red-500"
                  }`}
                >
                  {atomikaEftirLaufid >= 0 ? "" : "−"}
                  {fmtKr(Math.abs(atomikaEftirLaufid))}
                </p>
                <p className="mt-0.5 text-[10px] text-blue-500">
                  Eftir Laufið + sölukostnað
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400">
            Kveiktu á toggle til að sjá hvað Laufið myndi fá ef þeir selja
            þennan samning.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Senda í CRM ─────────────────────────────────────────────────────────────

function SavedScenarios({
  scenarios,
  onLoad,
  onDelete,
}: {
  scenarios: Scenario[];
  onLoad: (inputs: CalcInputs, customerName: string) => void;
  onDelete: (id: number) => void;
}) {
  if (scenarios.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-10 text-center">
        <div className="mb-3 text-4xl">📄</div>
        <h3 className="mb-1 font-semibold text-slate-800">
          Engar vistaðar áætlanir
        </h3>
        <p className="text-sm text-slate-500">
          Stofnaðu viðskiptavin í Laufinu úr Tilboðsgerð til að vista áætlun.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {scenarios.map((s) => {
        const results = s.results;
        return (
          <div
            key={s.id}
            className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate font-semibold text-slate-900">
                  {s.customerName}
                </h3>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  {new Date(s.createdAt).toLocaleDateString("is-IS", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
              <button
                onClick={() => onDelete(s.id)}
                className="p-1 text-slate-300 transition-colors hover:text-red-500"
                aria-label="Eyða"
                title="Eyða áætlun"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <dl className="flex-1 space-y-2 text-sm">
              <ScenarioStat
                label="Ávinningur viðsk. (heild)"
                value={fmtKr(results.totalCustomerBenefit)}
                strong
              />
              <ScenarioStat
                label="Ávinningur viðsk. 1. ár"
                value={fmtKr(results.year1CustomerBenefit)}
              />
              <ScenarioStat
                label="Tekjur Atomika (heild)"
                value={fmtKr(results.totalCompanyRevenue)}
              />
            </dl>

            {s.notes && (
              <p className="mt-3 line-clamp-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                {s.notes}
              </p>
            )}

            <button
              onClick={() => onLoad(s.inputs, s.customerName)}
              className="mt-4 w-full rounded-lg py-2 text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: ATOMIKA_BLUE }}
            >
              Opna í reiknivél
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ScenarioStat({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd
        className={
          strong ? "font-bold text-slate-900" : "font-medium text-slate-700"
        }
      >
        {value}
      </dd>
    </div>
  );
}

// ─── Smá-íhlutir ─────────────────────────────────────────────────────────────
